import { FormattedMessage, useIntl } from 'react-intl';
import Modal from '@/shared/components/Modal';
import Spinner from '@/shared/components/Spinner';
import { useScheduleActivities } from '../hooks/useScheduleActivities';
import { translateDetailRows } from '../scheduleActivityDetails';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: number;
}

export default function ScheduleActivitiesModal({ isOpen, onClose, scheduleId }: Props) {
  const intl = useIntl();
  const { data: activities, isLoading } = useScheduleActivities(scheduleId, isOpen);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={intl.formatMessage({ id: 'schedule.activities.title' })}
      eyebrow={intl.formatMessage({ id: 'schedule.activities.eyebrow' })}
    >
      {isLoading && (
        <div className="flex items-center gap-2 py-8 justify-center text-sm text-slate-500">
          <Spinner className="h-4 w-4" />
          <FormattedMessage id="schedule.activities.loading" />
        </div>
      )}
      {!isLoading && (!activities || activities.length === 0) && (
        <p className="py-8 text-center text-sm text-slate-500">
          <FormattedMessage id="schedule.activities.empty" />
        </p>
      )}
      {!isLoading && activities && activities.length > 0 && (
        <div className="space-y-3">
          {activities.map((activity) => {
            const rows = translateDetailRows(intl, activity.details);
            return (
              <div key={activity.id} className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {intl.formatMessage({ id: `schedule.activities.type.${activity.activity_type}`, defaultMessage: activity.activity_type })}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(activity.created_at).toLocaleString()}
                  </span>
                </div>
                {rows.length > 0 && (
                  <dl className="mt-2 space-y-1">
                    {rows.map((row) => (
                      <div key={row.key} className="flex gap-2 text-xs">
                        <dt className="font-medium text-slate-500">{row.label}:</dt>
                        <dd className="text-slate-600">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
